import { useRef } from 'react';
import { isBrowser, isWindows } from 'react-device-detect';
import { useLocalStorageState } from 'ahooks';
import { CALLBACK_MESSAGE } from '@/constants/config';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import type { socialLoginPayload } from '@iecf/core';
import { useBizState } from './use-biz-state';
import { useCore } from './use-core';
import { useCoreRequest } from './use-core-request';
import { useFlow } from './use-flow';

export const getCallbackKey = (code: string, state: string) => `${code}:${state}`;

export type CallbackMessage = {
  type: typeof CALLBACK_MESSAGE;
  data: socialLoginPayload;
};

export const useSocialLogin = () => {
  const { setFlow } = useFlow();
  const { setBizState } = useBizState();
  const socialWindowRef = useRef<Window | null>(null);
  const watchTimer = useRef<number | null>(null);
  const isOpenWindow = isBrowser && isWindows;
  const [, setSocialCallbackKey] = useLocalStorageState<string>('socialCallbackKey');
  const [, setOriginURI] = useLocalStorageState('originURI');
  const { lenovoId, lang } = useCore();

  const resetSocialRedirecting = () => {
    setBizState({ socialAuthRedirecting: false });
  };

  const getSocialCallbackUrl = (thirdName: string) => {
    return window.location.origin + import.meta.env.VITE_PUBLIC_PATH + `thirdname/${thirdName}`;
  };

  const stopWindowWatch = () => {
    if (watchTimer.current) {
      window.clearInterval(watchTimer.current);
      watchTimer.current = null;
    }
  };

  const watchSocialWindow = () => {
    stopWindowWatch();
    watchTimer.current = window.setInterval(() => {
      if (!socialWindowRef.current || socialWindowRef.current.closed) {
        stopWindowWatch();
        socialWindowRef.current = null;
        resetSocialRedirecting();
      }
    }, 500);
  };

  const { loading: socialRedirectLoading, run: runSocialRedirect } = useCoreRequest(
    (thirdName: string) => {
      return lenovoId.socialRedirect({
        thirdName,
        lang: lang,
        cb: getSocialCallbackUrl(thirdName),
      });
    },
    {
      manual: true,
      onBefore: () => {
        setBizState({ socialAuthRedirecting: !isOpenWindow });
      },
      onSuccess: ({ data }) => {
        if (data.thirdAuthenUrl) {
          setOriginURI(window.location.href);
          if (isOpenWindow) {
            const newWindow = window.open(data.thirdAuthenUrl, 'social login', 'width=640,height=720');
            if (!newWindow) {
              resetSocialRedirecting();
              return;
            }
            socialWindowRef.current = newWindow;
            watchSocialWindow();
          } else {
            window.location.href = data.thirdAuthenUrl;
          }
          return;
        }
        resetSocialRedirecting();
      },
      onError: () => {
        resetSocialRedirecting();
      },
    },
  );

  const { loading: socialLoginLoading, run: runSocialLogin } = useCoreRequest(
    (params: socialLoginPayload) => {
      setSocialCallbackKey(getCallbackKey(params.code, params.state));
      setBizState({ socialAuthorizing: true });
      return lenovoId.socialLogin(params);
    },
    {
      manual: true,
      onSuccess: ({ data, nextSteps }) => {
        setBizState({ ...data, socialAuthorizing: false });
        if (nextSteps === Flow.LOGIN_STEP_SUCCESS_CALLBACK) {
          if (data.callBack) {
            window.location.href = data.callBack;
            return;
          }
        }
      },
      onError: (code) => {
        setBizState({ socialAuthorizing: false, socialAuthRedirecting: false });
        switch (code) {
          case ErrorCode.SocialLoginNoEmail:
            setFlow(Flow.LOGIN_STEP_SOCIAL_UNAUTHORIZED);
            return true;
          case ErrorCode.SocialLoginInvalidToken:
          case ErrorCode.SocialLoginTimeout:
          case ErrorCode.RequestTimeout:
            setFlow(Flow.LOGIN_STEP_SOCIAL_ERROR);
            return true;
        }
        return true;
      },
    },
  );

  const doSocialLoginCallback = (data: socialLoginPayload) => {
    if (isOpenWindow) {
      const message: CallbackMessage = {
        data,
        type: CALLBACK_MESSAGE,
      };
      window.opener?.postMessage(message, window.location.origin);
    } else {
      if (!data.code || !data.state) {
        resetSocialRedirecting();
        setFlow(Flow.LOGIN_STEP_SOCIAL_ERROR);
      } else {
        runSocialLogin(data);
      }
    }
  };

  const closeSocialWindow = () => {
    stopWindowWatch();
    try {
      if (socialWindowRef.current && !socialWindowRef.current.closed) {
        socialWindowRef.current.close();
      }
    } catch {
      // do nothing
    }
    socialWindowRef.current = null;
  };

  return {
    doSocialLoginCallback,
    runSocialRedirect,
    runSocialLogin,
    closeSocialWindow,
    socialWindowRef,
    socialLoginLoading,
    socialRedirectLoading,
    isOpenWindow,
  };
};
